// Sending on a phone: the typed text travels from the composer to its
// place in the conversation at its exact size, while the messages above
// slide up by the room the new one takes. Same view-transition approach
// as opening a thread (see `ThreadLayer`).
//
// The new message's row is the moving picture; it starts with its text
// where the composer showed it and lands where the list renders it. The list is
// a second picture: the old and new states slide together, so rows that
// exist in both overlap exactly and nothing cross-fades.

import { flushSync } from "react-dom";

const TEXT = "chat-send";
const LIST = "chat-send-list";
/** Marks the scroll containers a sent message can land in. */
export const SEND_LIST_ATTR = "data-send-list";

const DURATION_MS = 360;
const EASE = "cubic-bezier(0.32, 0.72, 0, 1)";
/** Longest wait for the message row to render before the pictures are taken. */
const ROW_WAIT_MS = 250;

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

export function canSendTransition(): boolean {
  return (
    typeof document !== "undefined" &&
    typeof document.startViewTransition === "function" &&
    !prefersReducedMotion()
  );
}

/** The composer's own list: the nearest one sharing an ancestor with it
 *  (a thread's list before the room's beneath it). */
function listAbove(textarea: HTMLElement): HTMLElement | null {
  for (let el = textarea.parentElement; el; el = el.parentElement) {
    const list = el.querySelector<HTMLElement>(`[${SEND_LIST_ATTR}]`);
    if (list) return list;
  }
  return null;
}

function rowTops(list: HTMLElement): Map<string, number> {
  const tops = new Map<string, number>();
  const box = list.getBoundingClientRect();
  for (const row of list.querySelectorAll<HTMLElement>("[data-event-id]")) {
    const top = row.getBoundingClientRect().top;
    if (top >= box.top && top <= box.bottom) tops.set(row.dataset.eventId ?? "", top);
  }
  return tops;
}

// Rendering is suspended while the update callback runs, so wait on DOM
// mutations and timers, never on animation frames.
function waitForNewRow(list: HTMLElement, known: Set<string>): Promise<HTMLElement | null> {
  const find = () => {
    const rows = list.querySelectorAll<HTMLElement>("[data-event-id]");
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      const row = rows[i]!;
      if (!known.has(row.dataset.eventId ?? "")) return row;
    }
    return null;
  };
  return new Promise((resolve) => {
    const found = find();
    if (found) return resolve(found);
    const observer = new MutationObserver(() => {
      const row = find();
      if (!row) return;
      observer.disconnect();
      clearTimeout(timer);
      resolve(row);
    });
    observer.observe(list, { childList: true, subtree: true });
    const timer = setTimeout(() => {
      observer.disconnect();
      resolve(null);
    }, ROW_WAIT_MS);
  });
}

type Placement = { dx: number; dy: number; width: number };

const settle = () => new Promise<void>((resolve) => setTimeout(resolve, 0));

function motionCss(text: Placement | null, shift: number): string {
  const rules = [
    `html[data-send-transition]::view-transition-old(root) { animation: none; opacity: 0; }`,
    `html[data-send-transition]::view-transition-new(root) { animation: none; }`,
    `::view-transition-group(${LIST}) { animation: none; overflow: clip; }`,
    `::view-transition-old(${LIST}), ::view-transition-new(${LIST}) { mix-blend-mode: normal; animation-duration: ${DURATION_MS}ms; animation-timing-function: ${EASE}; animation-fill-mode: both; }`,
    `::view-transition-old(${LIST}) { animation-name: chat-send-list-old; }`,
    `::view-transition-new(${LIST}) { animation-name: chat-send-list-new; }`,
    `@keyframes chat-send-list-old { from { transform: none; } to { transform: translateY(${-shift}px); } }`,
    `@keyframes chat-send-list-new { from { transform: translateY(${shift}px); } to { transform: none; } }`,
  ];
  if (text) {
    rules.push(
      `::view-transition-group(${TEXT}) { animation-duration: ${DURATION_MS}ms; animation-timing-function: ${EASE}; }`,
      `::view-transition-old(${TEXT}) { animation: none; opacity: 0; }`,
      `::view-transition-new(${TEXT}) { inline-size: ${text.width}px; block-size: auto; animation: chat-send-text ${DURATION_MS}ms ${EASE} both; }`,
      `@keyframes chat-send-text { from { transform: translate(${text.dx}px, ${text.dy}px); } to { transform: none; } }`,
    );
  }
  return rules.join("\n");
}

/**
 * Clear the composer and start the send inside one view transition.
 * `clear` must empty the composer synchronously; `send` starts the send
 * and is called exactly once. Resolves or rejects with the send.
 */
export function sendWithTransition(
  textarea: HTMLTextAreaElement,
  clear: () => void,
  send: () => Promise<void>,
): Promise<void> {
  const list = canSendTransition() ? listAbove(textarea) : null;
  if (!list) {
    clear();
    return send();
  }
  const field = textarea.getBoundingClientRect();
  const fieldStyle = getComputedStyle(textarea);
  const textLeft =
    field.left + parseFloat(fieldStyle.paddingLeft) + parseFloat(fieldStyle.borderLeftWidth);
  const textTop =
    field.top + parseFloat(fieldStyle.paddingTop) + parseFloat(fieldStyle.borderTopWidth);
  return transitionInto(list, textarea, clear, send, (row, landed) => {
    const landedBody = row.querySelector<HTMLElement>("[data-message-body]");
    if (!landedBody) return null;
    // The whole row travels (avatar and name with it), placed so its
    // text starts exactly where the composer showed it.
    const bodyBox = landedBody.getBoundingClientRect();
    return {
      dx: Math.round(textLeft - field.left - (bodyBox.left - landed.left)),
      dy: Math.round(textTop - field.top - (bodyBox.top - landed.top)),
      width: Math.round(landed.width),
    };
  });
}

/**
 * Start a send whose message appears in `list` without typed text (a
 * photo or file): the new row rises from the composer below the list
 * while the rows above slide up, as a sent text does.
 */
export function arriveWithTransition(
  list: HTMLElement | null,
  send: () => Promise<void>,
): Promise<void> {
  if (!list || !canSendTransition()) return send();
  const bottom = list.getBoundingClientRect().bottom;
  return transitionInto(
    list,
    null,
    () => {},
    send,
    (_row, landed) => ({
      dx: 0,
      dy: Math.round(bottom - landed.top),
      width: Math.round(landed.width),
    }),
  );
}

function transitionInto(
  list: HTMLElement,
  textarea: HTMLTextAreaElement | null,
  clear: () => void,
  send: () => Promise<void>,
  place: (row: HTMLElement, landed: DOMRect) => Placement | null,
): Promise<void> {
  const before = rowTops(list);
  const known = new Set(
    [...list.querySelectorAll<HTMLElement>("[data-event-id]")].map(
      (row) => row.dataset.eventId ?? "",
    ),
  );

  const root = document.documentElement;
  const style = document.createElement("style");
  let moving: HTMLElement | null = null;
  let sending: Promise<void> | null = null;
  const start = () => {
    sending ??= send();
    return sending;
  };

  if (textarea) textarea.style.viewTransitionName = TEXT;
  list.style.viewTransitionName = LIST;
  root.dataset.sendTransition = "";

  const transition = document.startViewTransition(async () => {
    if (textarea) textarea.style.viewTransitionName = "";
    flushSync(clear);
    void start().catch(() => {});
    const row = await waitForNewRow(list, known);
    // Let the list commit its scroll and measurement for the new row.
    await settle();
    let shift = 0;
    for (const [id, top] of before) {
      const row = list.querySelector<HTMLElement>(`[data-event-id="${CSS.escape(id)}"]`);
      if (!row) continue;
      shift = top - row.getBoundingClientRect().top;
      break;
    }
    const placement = row ? place(row, row.getBoundingClientRect()) : null;
    if (row && placement) {
      moving = row;
      row.style.viewTransitionName = TEXT;
    }
    style.textContent = motionCss(placement, Math.round(shift));
    document.head.appendChild(style);
  });

  const cleanup = () => {
    if (textarea) textarea.style.viewTransitionName = "";
    list.style.viewTransitionName = "";
    if (moving) moving.style.viewTransitionName = "";
    delete root.dataset.sendTransition;
    style.remove();
  };
  void transition.finished.then(cleanup, cleanup);

  return transition.updateCallbackDone.then(start, (err: unknown) => {
    // The update threw before or after starting the send; never drop it.
    console.warn("send transition update failed", err);
    clear();
    return start();
  });
}
